<script>
  import { createNamespacedHelpers } from 'vuex';
  import axios from 'axios';
  import { eventBus } from '../../main';
  import { appendHoneypotFields } from '../../utils/appendHoneypot';

  const { mapGetters } = createNamespacedHelpers('hitch');

  export default {
    name: 'AddToCart',
    props: {
      sku: {
        /** product bundle sku */
        type: String,
        required: true
      },
      title: {
        /** entry title */
        type: String,
        required: true
      },
      entryId: {
        /** Craft entry ID */
        type: Number,
        required: true
      },
      redirectUrl: {
        /** store cart redirect url */
        type: String,
        required: true
      },
      /** product bundle type ID */
      bundleId: {
        type: [Number, String],
        default: 4
      },
      /** Promotion Categories on customizable product */
      promotionCategories: {
        type: String,
        default: '',
      }
    },
    data() {
      return {
        quantity: 1,
        bundleData: null,
        totalPrice: 0,
        disableCartBtn: false,
        addToCartButtonText: 'Add To Cart',
      };
    },
    computed: {
      ...mapGetters([
        'getCsrfToken',
        'getSelectedVariant',
        'getUserText',
        'getCustomizationStyle',
        'getLargeLetterStyle',
        'getSmallLetterStyle',
        'getLoadingLargeLetters',
        'getLoadingSmallLetters',
        'getBundledProducts',
        'getUserText',
        'getAllProducts',
        'getEntryId'
      ]),
      /** Returns the current character count of the users string */
      letterCount: {
        get() {
          return this.getUserText.length;
        }
      },
      /** Gets the total price from the selected base product variant */
      getTotalPrice() {
        let totalPrice = 0;
        totalPrice = (this.getSelectedVariant.price > this.getSelectedVariant.salePrice) ? this.getSelectedVariant.salePrice : this.getSelectedVariant.price;
        return totalPrice.toFixed(2);
      }

    },
    methods: {
      /** Submits the bundle to create it dynamically in Craft */
      submitCart() {
        this.disableCartBtn = true;
        this.addToCartButtonText = 'Adding...';
        const formData = new FormData();
        formData.append('CRAFT_CSRF_TOKEN',this.getCsrfToken);
        formData.append('action', 'bundle-mate/default/make-bundle');
        formData.append('typeId', this.bundleId);
        formData.append('quantity',this.quantity);
        formData.append('title', this.title);
        formData.append('sku', this.sku);
        formData.append('promotionCategories', this.promotionCategories);
        this.getBundledProducts.forEach((product) => {
          formData.append('products[]', product.purchasableId);
        });
        this.getBundledProducts.forEach((product) => {
          formData.append(`qty[${product.purchasableId}]`, product.qty);
        });
        appendHoneypotFields(formData);
        axios.post('/', formData)
          .then((response) => {
            this.addToCart(response.data);
        });
      },
      /** Adds the newly created bundle to the users cart */
      addToCart(bundle) {
        const formData = new FormData();
        const data = {
          csrf: this.getCsrfToken,
          action: 'commerce/cart/update-cart',
          cartUpdatedNotice: 'Added to cart.',
          qty: parseInt(this.quantity),
          purchasableId: bundle.bundleId,
          entryId: this.entryId,
          note: this.getUserText,
          options: {
            'frame': this.getSelectedVariant.name,
            'letters': this.getUserText,
            'customizationType': this.getCustomizationStyle.label,
            'entryId': this.entryId
          }
        };

        data.options.letterStyle = (this.getCustomizationStyle.value === 'double') ?
             this.getLargeLetterStyle.name : 
             `${this.getSmallLetterStyle.name},${this.getLargeLetterStyle.name}`;

        formData.append('CRAFT_CSRF_TOKEN',data.csrf);
        formData.append('action',data.action);
        formData.append('cartUpdatedNotice',data.cartUpdatedNotice);
        formData.append('qty',data.qty);
        formData.append('purchasableId',data.purchasableId);
        formData.append('entryId',data.entryId);
        formData.append('note',data.note);
        formData.append('options[frame]',data.options.frame);
        formData.append('options[letterStyle]',data.options.letterStyle);
        formData.append('options[customizationType]',data.options.customizationType);
        formData.append('options[letters]',data.options.letters);
        formData.append('options[entryId]',data.options.entryId);
        appendHoneypotFields(formData);

        axios.post('/', formData)
          .then(() => {
            this.disableCartBtn = false;
            window.location.href = this.redirectUrl;
          });
      }
    },
    created() {
      eventBus.$on('new-data', (data) => {
        this.bundleData = data;
      });
    },
  };
</script>

<template>
  <div class="product-view__cart">
    <nav v-if="getAllProducts.length > 1" class="product-nav product-view__nav">
      <h3 class="product-nav__heading">Personalize Your Ride</h3>
      <h6 class="product-nav__subheading">Select a product below</h6>
      <ul>
        <li v-for="product in getAllProducts" :key="product.id">
          <a
            v-if="product.id !== getEntryId"
            :href="product.url"
            :style="`background-image: url('${product.thumbnail}');`"
            class="product-nav__link"
          >
            <span class="product-nav__label">{{ product.title }}</span>
          </a>
          <span
            v-else
            :style="`background-image: url('${product.thumbnail}');`"
            class="product-nav__current"
          >
            <span class="product-nav__label">{{ product.title }}</span>
          </span>
        </li>
      </ul>
    </nav>

    <h2 class="product-view__aside-heading">{{ title }}</h2>

    <form class="product-cart--letters product-view__add-to-cart js-add-item" @submit.prevent="submitCart" accept-charset="UTF-8">
      <input type="hidden" class="js-custom-plate__bulk-discount" />
      <h2 class="product-cart__price js-custom-plate__price-display-total">${{ getTotalPrice }}</h2>

      <div class="sidebar-inputs">
        <slot name="inputs" />
      </div>

      <em class="product-cart__chars-counter">
        <span class="js-custom-plate__price-display-count">{{ letterCount }}</span>
        character<span v-show="letterCount === 0 || letterCount > 1">s</span>
      </em>

      <div class="product-cart__bulk">
        <slot name="bulkPricing"></slot>

        <div class="product-cart__bulk-qty">
          <input
            v-model="quantity"
            type="text"
            class="js-custom-plate__qty"
            name="qty"
            id="qty"
            placeholder="Qty"
            value="1"
          />
        </div>
      </div>

      <div v-if="getUserText.length > 0" class="product-cart__cta">
        <input
          class="button--primary m-full js-custom-plate__add js-check-add js-add-button"
          type="submit"
          name="submit_this"
          :value="addToCartButtonText"
          :disabled="disableCartBtn"
        />
      </div>
    </form>
    <slot name="shipping"></slot>
    <slot name="widgets"></slot>
  </div>
</template>

<style scoped>
  .sidebar-inputs {
    margin-top: 20px;
  }
  .sidebar-inputs >>> * {
    margin-top: 5px;
  }
  .sidebar-inputs >>> * :first-child {
    margin-top: 0;
  }
</style>